import React, { useMemo, useState } from 'react'
import { ChevronDownIcon, UserCircleIcon } from 'lucide-react'
import type { InboxAssignee } from '../../types/inbox'
import { assigneeDisplayName } from '../../lib/inboxPermissions'

interface AssigneePickerProps {
  assignees: InboxAssignee[]
  value: string | null
  onChange: (userId: string | null) => void
  canAssignOthers: boolean
  currentUserId?: string | null
  compact?: boolean
  avatarOnly?: boolean
  disabled?: boolean
  className?: string
}

function assigneeInitials(user: {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
}): string {
  const first = user.first_name?.[0] || ''
  const last = user.last_name?.[0] || ''
  if (first || last) return (first + last).toUpperCase()
  return (user.email?.[0] || '?').toUpperCase()
}

export function AssigneePicker({
  assignees,
  value,
  onChange,
  canAssignOthers,
  currentUserId,
  compact,
  avatarOnly,
  disabled,
  className = '',
}: AssigneePickerProps) {
  const [open, setOpen] = useState(false)

  const selected = useMemo(
    () => assignees.find((a) => a.id === value) || null,
    [assignees, value]
  )

  const options = useMemo(() => {
    if (canAssignOthers) return assignees
    return assignees.filter((a) => a.id === currentUserId)
  }, [assignees, canAssignOthers, currentUserId])

  const label = selected ? assigneeDisplayName(selected) : 'Unassigned'

  const triggerClass = avatarOnly
    ? 'inline-flex items-center justify-center w-7 h-7 rounded-full border border-gray-200 hover:bg-gray-50 disabled:opacity-50'
    : `inline-flex items-center gap-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 ${
        compact ? 'px-2 py-1 text-[11px]' : 'px-2.5 py-1.5 text-xs'
      } font-medium text-gray-700`

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={label}
        className={triggerClass}
      >
        {avatarOnly ? (
          selected ? (
            <span className="w-full h-full rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold flex items-center justify-center">
              {assigneeInitials(selected)}
            </span>
          ) : (
            <span className="w-full h-full rounded-full bg-gray-100 text-gray-400 text-xs font-semibold flex items-center justify-center">
              ?
            </span>
          )
        ) : (
          <>
            <UserCircleIcon className={compact ? 'w-3.5 h-3.5 text-gray-500' : 'w-4 h-4 text-gray-500'} />
            <span className="truncate max-w-[8rem]">{label}</span>
            <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
          </>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-50 w-52 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-64 overflow-y-auto">
            {canAssignOthers && (
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 text-gray-600"
                onClick={() => {
                  onChange(null)
                  setOpen(false)
                }}
              >
                Unassigned
              </button>
            )}
            {currentUserId && !canAssignOthers && (
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 text-blue-700"
                onClick={() => {
                  onChange(currentUserId)
                  setOpen(false)
                }}
              >
                Claim to me
              </button>
            )}
            {options.map((user) => (
              <button
                key={user.id}
                type="button"
                className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                  user.id === value ? 'bg-blue-50 text-blue-700' : 'text-gray-800'
                }`}
                onClick={() => {
                  onChange(user.id)
                  setOpen(false)
                }}
              >
                {assigneeDisplayName(user)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
